/**
 * GDN Variant Bundle Detail Accordion
 *
 * Aktualisiert das „Was ist im X enthalten?“-Akkordeon dynamisch,
 * wenn die Variante über die Detail-Tiles gewechselt wird.
 *
 * - Liest Bundle-Items + Variant-Titel aus den data-Attributen der Tile-Labels.
 * - Aktualisiert die Liste, den Header-Text und blendet das Akkordeon aus,
 *   wenn die gewählte Variante keine Bundle-Items besitzt.
 * - Setzt zusätzlich die `is-selected`-Klasse, damit das Border-Styling
 *   (CSS-Sibling-Selektor + Klassen-Fallback) auch nach DOM-Re-Renders greift.
 */
(function () {
  'use strict';

  const TILE_LIST_SELECTOR = '[data-variant-detail-list]';
  const ACCORDION_SELECTOR = '[data-variant-bundle-detail]';
  const TILE_SELECTOR = '.variant-detail-tile';
  const RADIO_SELECTOR = '.variant-detail-tile__radio';
  const TITLE_SELECTOR = '[data-bundle-detail-title]';
  const LIST_SELECTOR = '[data-bundle-detail-list]';
  const VARIANTS_DATA_SELECTOR = 'script[data-variant-detail-data]';

  let lastDispatchedVariantId = null;
  let suppressOwnEvent = false;

  function getVariantsData(formId) {
    const selector = formId
      ? `${VARIANTS_DATA_SELECTOR}[data-variant-detail-data="${formId}"]`
      : VARIANTS_DATA_SELECTOR;
    const script = document.querySelector(selector);
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return [];
    }
  }

  function dispatchVariantChange(formId, variantId) {
    if (!formId || !variantId) return;
    if (lastDispatchedVariantId === String(variantId)) return;
    const form = document.forms[formId] || document.getElementById(formId);
    if (!form) return;
    const variants = getVariantsData(formId);
    const variant = variants.find(
      (v) => String(v.id) === String(variantId)
    );
    if (!variant) return;
    lastDispatchedVariantId = String(variantId);
    suppressOwnEvent = true;
    const detail = { formId: form.id || formId, variant, previousVariant: null };
    try {
      form.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail })
      );
      form.dispatchEvent(
        new CustomEvent('variant:changed', { bubbles: true, detail })
      );
    } finally {
      setTimeout(() => {
        suppressOwnEvent = false;
      }, 0);
    }
  }

  function safeJsonParse(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function findTileForVariantId(list, variantId) {
    if (!list || !variantId) return null;
    return list.querySelector(
      `${TILE_SELECTOR}[data-variant-id="${variantId}"]`
    );
  }

  function findCheckedTile(list) {
    if (!list) return null;
    const checked = list.querySelector(`${RADIO_SELECTOR}:checked`);
    if (!checked) return null;
    return checked.nextElementSibling && checked.nextElementSibling.matches(TILE_SELECTOR)
      ? checked.nextElementSibling
      : list.querySelector(`label[for="${checked.id}"]`);
  }

  function updateSelectedState(list) {
    if (!list) return;
    const tiles = list.querySelectorAll(TILE_SELECTOR);
    tiles.forEach((tile) => tile.classList.remove('is-selected'));
    const checkedTile = findCheckedTile(list);
    if (checkedTile) checkedTile.classList.add('is-selected');
  }

  function updateAccordion(accordion, tile) {
    if (!accordion || !tile) return;

    const variantTitle = tile.getAttribute('data-variant-title') || '';
    const items = safeJsonParse(tile.getAttribute('data-bundle-items'));

    const titleEl = accordion.querySelector(TITLE_SELECTOR);
    const listEl = accordion.querySelector(LIST_SELECTOR);

    if (titleEl) {
      titleEl.textContent = variantTitle
        ? `Was ist im ${variantTitle} enthalten?`
        : 'Was ist enthalten?';
    }

    if (listEl) {
      listEl.innerHTML = '';
      items.forEach((item) => {
        if (!item) return;
        const li = document.createElement('li');
        li.className = 'variant-bundle-detail__item';
        li.textContent = item;
        listEl.appendChild(li);
      });
    }

    if (items.length === 0) {
      accordion.classList.add('is-hidden');
      if (accordion.tagName === 'DETAILS') accordion.open = false;
    } else {
      accordion.classList.remove('is-hidden');
    }
  }

  function findRelatedAccordion(list) {
    if (!list) return null;
    const formId = list.getAttribute('data-form-id');
    if (formId) {
      const scoped = document.querySelector(
        `${ACCORDION_SELECTOR}[data-form-id="${formId}"]`
      );
      if (scoped) return scoped;
    }
    if (list.parentElement) {
      const sibling = list.parentElement.querySelector(ACCORDION_SELECTOR);
      if (sibling) return sibling;
    }
    return document.querySelector(ACCORDION_SELECTOR);
  }

  function handleRadioChange(event) {
    const radio = event.target;
    if (!radio || !radio.matches || !radio.matches(RADIO_SELECTOR)) return;
    const list = radio.closest(TILE_LIST_SELECTOR);
    if (!list) return;
    updateSelectedState(list);
    const checkedTile = findCheckedTile(list);
    const accordion = findRelatedAccordion(list);
    if (accordion && checkedTile) updateAccordion(accordion, checkedTile);

    const formId = list.getAttribute('data-form-id');
    const variantId =
      (checkedTile && checkedTile.getAttribute('data-variant-id')) ||
      radio.value;
    dispatchVariantChange(formId, variantId);
  }

  function handleVariantEvent(event) {
    if (suppressOwnEvent) return;
    const variantId =
      event && event.detail && event.detail.variant && event.detail.variant.id;
    if (!variantId) return;
    lastDispatchedVariantId = String(variantId);
    document.querySelectorAll(TILE_LIST_SELECTOR).forEach((list) => {
      const tile = findTileForVariantId(list, variantId);
      if (!tile) return;
      updateSelectedState(list);
      const accordion = findRelatedAccordion(list);
      if (accordion) updateAccordion(accordion, tile);
    });
  }

  function init() {
    document.addEventListener('change', handleRadioChange, true);
    document.addEventListener('variant:change', handleVariantEvent);
    document.addEventListener('variant:changed', handleVariantEvent);

    document.querySelectorAll(TILE_LIST_SELECTOR).forEach((list) => {
      updateSelectedState(list);
      const checkedTile = findCheckedTile(list);
      const accordion = findRelatedAccordion(list);
      if (accordion && checkedTile) updateAccordion(accordion, checkedTile);
      if (checkedTile) {
        const formId = list.getAttribute('data-form-id');
        const variantId = checkedTile.getAttribute('data-variant-id');
        if (formId && variantId) {
          lastDispatchedVariantId = String(variantId);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
